import { types, getEnv, SnapshotOut, Instance } from 'mobx-state-tree';
import { LiveEvents, DesktopLiveEventListStore, IDesktopLiveEventListStore, IGameGroupStore } from 'eTip--common';
import { createComparerOfSorting, SortType } from 'easit--utils';

export interface IStreamData { isMatchTracker: boolean, isVideo: boolean }

export const LiveCategorySelectedGameGroupStore = types
    .model({
        id: types.string,
        name: types.string
    });

/**
 * For live category in list and left column
 */
export const LiveCategoryStore = types
    .model({
        forceCanConvertTipName: types.optional(types.boolean, false), // for Desktop live false, but for live in prematch set to true
        isFavorite: types.boolean,
        id: types.number,
        sequence: types.number,
        categoryPathName: types.maybeNull(types.string),
        events: types.optional(types.array(DesktopLiveEventListStore), []),
        graphicsPresentationId: types.number,
        name: types.string,
        isExpandedInLeftMenu: types.optional(types.boolean, true),
        //isEventPopUpVisible: types.optional(types.boolean, false),
        shownGameGroups: types.optional(types.array(LiveCategorySelectedGameGroupStore), []),
        shownGameGroupId: types.maybeNull(types.string),
        hasMatchTrackerEvent: types.optional(types.boolean, false),
        hasVideoEvent: types.optional(types.boolean, false)
    }).views(self => {
        return {
            get eventCount(): number {
                return self.events.length;
            }
        }
    })
    .actions(self => {

        let env = getEnv(self);

        const updateCategoryFromApi = (_sourceCategory: LiveEvents.ReturnValueItem): void => {
            //categoryPathName, name probably not changing
            // eventCount need to be evaluated due to favorites
        }

        let sequenceComparer = createComparerOfSorting((g: IGameGroupStore) => g.sequence, SortType.Asc);
        let eventComparer = (g1: IDesktopLiveEventListStore, g2: IDesktopLiveEventListStore) => {
            // sequence is compound Region/Tournament sequence

            if (g1.sequence < g2.sequence) {
                return -1;
            }
            if (g1.sequence > g2.sequence) {
                return 1;
            }

            //from oldest asc

            if (!g1.startDate && g2.startDate) {
                return -1;
            }

            if (g1.startDate && !g2.startDate) {
                return 1;
            }

            if ((!g1.startDate && !g2.startDate) || g1.startDate.getTime() == g2.startDate.getTime()) {

                if (g1.id < g2.id) {
                    return -1;
                }
                if (g1.id > g2.id) {
                    return 1;
                }
                return 0;
            }
            if (g1.startDate.getTime() < g2.startDate.getTime()) {
                return -1;
            }
            return 1;
        }

        /**
         @param touchedInFav to update a normal category, not to add a favorite event
         @returns object of touched ids
         */
        const updateEventsFromApi = (sourceList: LiveEvents.EventsItem[], graphicsPresentationId: number | null, streamData: IStreamData): Set<number> => {
            if (!sourceList) {
                self.events.clear();
                self.hasMatchTrackerEvent = false;
                self.hasVideoEvent = false;
                return null;
            }

            let hasMatchTrackerEvent = false;
            let hasVideoEvent = false;
            let touched: Set<number> = new Set<number>();
            sourceList.forEach(sourceEvt => {
                let dest = self.events.find(evt => evt.id == sourceEvt.ID);
                if (dest) {
                    dest.setData(sourceEvt, graphicsPresentationId);
                    hasMatchTrackerEvent ||= !!dest.matchTracker;
                    hasVideoEvent ||= !!dest.liveStream;
                    touched.add(sourceEvt.ID);
                    return;
                }
                dest = DesktopLiveEventListStore.create(<SnapshotOut<typeof DesktopLiveEventListStore>>{
                    sequence: sourceEvt.Sequence,
                    externalId: sourceEvt.ExternalID,
                    forceCanConvertTipName: self.forceCanConvertTipName
                }, env);
                dest.setData(sourceEvt, graphicsPresentationId);
                touched.add(sourceEvt.ID);
                self.events.push(dest);
                hasMatchTrackerEvent ||= !!dest.matchTracker;
                hasVideoEvent ||= !!dest.liveStream;
            });
            sortEvents();

            updateShownGameGroup();

            self.hasMatchTrackerEvent = hasMatchTrackerEvent;
            self.hasVideoEvent = hasVideoEvent;

            streamData.isMatchTracker ||= hasMatchTrackerEvent;
            streamData.isVideo ||= hasVideoEvent;

            return touched;
        }

        const sortEvents = (): void => {
            self.events.replace(self.events.sort(eventComparer));
        }

        const toggleIsExpandedInLeftMenu = (): void => {
            self.isExpandedInLeftMenu = !self.isExpandedInLeftMenu;
        }

        // const toggleEventsPopupVisible = (): void => {
        //     self.isEventPopUpVisible = !self.isEventPopUpVisible;
        // }

        const updateShownGameGroup = (): void => {
            let availableGroups = new Map<string, IGameGroupStore>();
            if (self.events.length) {
                self.events.map(evt => {
                    evt.gameGroups.map(g => {
                        if (!availableGroups.has(g.id)) {
                            availableGroups.set(g.id, g);
                        }
                    });
                });
            }
            let list: IGameGroupStore[] = Array.from(availableGroups.values()).sort(sequenceComparer);
            if (list.length > 3) {
                list.splice(3);
            }

            let touched = new Set<string>();
            list.forEach(v => {
                if (!self.shownGameGroups.find(g => g.id === v.id)) {
                    self.shownGameGroups.push(LiveCategorySelectedGameGroupStore.create(<Instance<typeof LiveCategorySelectedGameGroupStore>>{ id: v.id, name: v.name }));
                }
                touched.add(v.id);
            });
            let toRemove = self.shownGameGroups.filter(v => !touched.has(v.id));
            toRemove.forEach(v => self.shownGameGroups.remove(v));
            self.shownGameGroups.replace(self.shownGameGroups.sort(sequenceComparer));

            if (self.shownGameGroupId === null) {
                if (self.shownGameGroups.length) {
                    self.shownGameGroupId = self.shownGameGroups[0].id;
                }
            } else {
                if (!list.find(v => v.id === self.shownGameGroupId)) {
                    if (list.length) {
                        self.shownGameGroupId = self.shownGameGroups[0].id;
                    } else {
                        self.shownGameGroupId = null;
                    }
                }
            }
        }

        const setSelectedGameGroupId = (gameGroupId: string): void => {
            self.shownGameGroupId = gameGroupId;
        }

        const addEvent = (evt: IDesktopLiveEventListStore): void => {
            self.events.push(evt);
            sortEvents();
            updateShownGameGroup();
        }

        return {
            updateCategoryFromApi,
            updateEventsFromApi,
            toggleIsExpandedInLeftMenu,
            setSelectedGameGroupId,
            addEvent,
            //toggleEventsPopupVisible
        }
    });

export type ILiveCategoryStore = Instance<typeof LiveCategoryStore>;